/**
 * Экран сброса пароля `/reset` (Фаза 3, И3).
 *
 * Токен восстановления приходит в query `?token=…` (ссылка из письма); `App`
 * извлекает его и передаёт пропом `token`. Форма `{newPassword, confirmPassword}`
 * → [`resetPassword`](../auth.ts) (`POST /api/auth/reset`); успех → `onSuccess`
 * (редирект на `/login`). 400 — ссылка недействительна/устарела/использована.
 *
 * Нет токена → понятное сообщение + ссылка запросить новую (`/forgot`).
 */
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api';
import { Link } from '../components/ui';
import { resetPassword } from '../auth';
import { MIN_PASSWORD_LENGTH } from './Register';

export interface ResetProps {
  /** Токен восстановления (из `?token=`). Если нет — экран покажет ошибку. */
  token?: string;
  /** Вызывается после успешной смены пароля (обычно — редирект на `/login`). */
  onSuccess?: () => void;
}

export function Reset({ token, onSuccess }: ResetProps): JSX.Element {
  const { t } = useTranslation();
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!token) {
    return (
      <main className="card card--page" aria-labelledby="reset-title">
        <h1 id="reset-title" className="page-title">
          {t('reset.titleNoToken')}
        </h1>
        <p className="error" role="alert" data-testid="reset-no-token">
          {t('reset.noToken')}
        </p>
        <p className="auth-switch">
          <Link to="/forgot">{t('reset.requestAgain')}</Link>
        </p>
        <p className="auth-switch">
          <Link to="/login">{t('nav.login')}</Link>
        </p>
      </main>
    );
  }

  // После раннего return выше TS сужает token до string; захватываем в const,
  // чтобы замыкание handleSubmit видело string (а не string | undefined).
  const resetToken = token;

  function validate(): string | null {
    if (newPassword.length < MIN_PASSWORD_LENGTH)
      return t('errors.passwordTooShort', { min: MIN_PASSWORD_LENGTH });
    if (newPassword !== confirm) return t('errors.passwordsMismatch');
    return null;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (submitting) return;
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await resetPassword(resetToken, newPassword);
      onSuccess?.();
    } catch (e) {
      setError(resetErrorMessage(t, e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="card card--page" aria-labelledby="reset-title">
      <h1 id="reset-title" className="page-title">
        {t('reset.title')}
      </h1>

      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <div className="form-row">
          <label className="field-label" htmlFor="reset-password">
            {t('reset.newPassword')}
          </label>
          <input
            id="reset-password"
            className="text-input"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="••••••••"
          />
          <span className="field-hint">
            {t('register.passwordHint', { min: MIN_PASSWORD_LENGTH })}
          </span>
        </div>

        <div className="form-row">
          <label className="field-label" htmlFor="reset-confirm">
            {t('common.confirmPassword')}
          </label>
          <input
            id="reset-confirm"
            className="text-input"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="••••••••"
          />
        </div>

        {error ? (
          <p className="error" role="alert" data-testid="reset-error">
            {error}
          </p>
        ) : null}

        <button type="submit" className="cta" disabled={submitting} data-testid="reset-submit">
          {submitting ? t('reset.saving') : t('reset.submit')}
        </button>
      </form>

      <p className="auth-switch">
        <Link to="/login">{t('nav.login')}</Link>
      </p>
    </main>
  );
}

/** Дружелюбное сообщение по статусу ошибки сброса (400/429/сеть). */
function resetErrorMessage(t: (key: string) => string, e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 400) return t('errors.resetLinkInvalid');
    if (e.status === 429) return t('errors.tooManyAttempts');
  }
  return t('errors.resetNetwork');
}
